import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { adminApi } from '../../api';
import type { ListPaymentsResult } from '../../types';
import { isAdmin } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle, getStoredRowsPerPage, setStoredRowsPerPage } from '../../hooks/core';
import { AdminBreadcrumbs } from '../../components/admin';

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function EntryTypeChip({ type }: { type: string }) {
  const colors: Record<string, 'success' | 'error' | 'info'> = {
    square_purchase: 'error',
    payout: 'success',
    manual_payment: 'info',
  };

  const labels: Record<string, string> = {
    square_purchase: 'Purchase',
    payout: 'Payout',
    manual_payment: 'Manual',
  };

  return <Chip label={labels[type] || type} color={colors[type] || 'default'} size="small" />;
}

export function PaymentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();

  useDocumentTitle('Payments');

  const [result, setResult] = useState<ListPaymentsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [entryTypeFilter, setEntryTypeFilter] = useState<string>(
    searchParams.get('entryType') || ''
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => getStoredRowsPerPage());

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminApi.listAllPayments({
        entryType: entryTypeFilter || undefined,
        page: page + 1,
        pageSize,
      });
      setResult(data);
    } catch (err) {
      setError('Failed to load payments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [entryTypeFilter, page, pageSize]);

  useEffect(() => {
    if (!isAdmin(currentUser)) {
      navigate('/');
      return;
    }
    fetchPayments();
  }, [currentUser, navigate, fetchPayments]);

  const handleEntryTypeChange = (value: string) => {
    setEntryTypeFilter(value);
    setPage(0);
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('entryType', value);
    } else {
      params.delete('entryType');
    }
    setSearchParams(params);
  };

  if (loading && !result) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <AdminBreadcrumbs items={[{ label: 'Payments' }]} />
      <Typography variant="h4" sx={{ mb: 3 }}>
        Payments
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Entry Type</InputLabel>
              <Select
                value={entryTypeFilter}
                label="Entry Type"
                onChange={(e) => handleEntryTypeChange(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="square_purchase">Purchases</MenuItem>
                <MenuItem value="payout">Payouts</MenuItem>
                <MenuItem value="manual_payment">Manual</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Board</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result?.payments.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>{formatDateTime(payment.createdAt)}</TableCell>
                  <TableCell>
                    <EntryTypeChip type={payment.entryType} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payment.userName || payment.user?.displayName || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payment.boardName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {payment.description}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: payment.amount >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 'bold',
                    }}
                  >
                    {payment.amount >= 0 ? '+' : ''}
                    {formatCurrency(payment.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {result && (
          <TablePagination
            component="div"
            count={result.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setPageSize(newSize);
              setStoredRowsPerPage(newSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </Card>
    </Box>
  );
}
